export const ROADMAP_FINANCE_STORAGE_KEY = 'roadmap_finance_workspace_v2';

export const resolveCompanyDocumentId = (company) => company?.document || company?.documentId || null;

export const resolveRoadmapBusinessRole = (roleName) => {
    if (roleName === 'superadmin') {
        return 'admin';
    }

    if (roleName === 'contabile' || roleName === 'operatore_contabile') {
        return 'contabile';
    }

    return 'commerciale';
};

export const getDocumentTypeLabel = (type) => {
    const labels = {
        supplier_invoice: 'Fattura fornitore',
        service_invoice: 'Fattura servizi',
        customer_invoice: 'Fattura cliente',
        nso_order: 'Ordine NSO/MSO',
        practice: 'Pratica',
        sts_export: 'Flusso STS',
    };

    return labels[type] || type;
};

export const getDocumentStatusLabel = (status) => {
    const labels = {
        da_esaminare: 'Da esaminare',
        da_agganciare: 'Da agganciare',
        contabilizzata: 'Contabilizzata',
        ricevuto: 'Ricevuto',
        preso_in_carico: 'Preso in carico',
        da_fatturare: 'Da fatturare',
        in_emissione: 'In emissione',
        fatturata: 'Fatturata',
        pronta_export: 'Pronta export',
        emessa: 'Emessa',
    };

    return labels[status] || status;
};

export const getDocumentStatusColor = (status) => {
    const colors = {
        da_esaminare: 'gold',
        da_agganciare: 'processing',
        contabilizzata: 'success',
        ricevuto: 'default',
        preso_in_carico: 'processing',
        da_fatturare: 'warning',
        in_emissione: 'processing',
        fatturata: 'success',
        pronta_export: 'purple',
        emessa: 'success',
    };

    return colors[status] || 'default';
};

export const getNotificationTone = (level) => {
    const tones = {
        info: 'processing',
        warning: 'warning',
        success: 'success',
        critical: 'error',
    };

    return tones[level] || 'default';
};

export const isFiscalYearClosed = (year, closedFiscalYears) => {
    return Array.isArray(closedFiscalYears) && closedFiscalYears.includes(year);
};

export const isDocumentVisibleToRole = (document, roleKey) => {
    if (roleKey === 'admin') {
        return true;
    }

    if (roleKey === 'contabile') {
        return document.domain !== 'commerciale';
    }

    return document.domain !== 'finance';
};

export const getCompanyLabel = (companyDocumentId, companies) => {
    return companies.find((company) => company.documentId === companyDocumentId)?.name || companyDocumentId;
};

export const buildFinanceQuickSearchEntries = ({
    documents,
    playbooks,
    companies,
    treasuryWorkspace,
    inventoryWorkspace,
    roleKey,
}) => {
    const visibleDocuments = documents.filter((document) => isDocumentVisibleToRole(document, roleKey));

    const documentEntries = visibleDocuments.map((document) => ({
        id: document.id,
        kind: 'document',
        title: document.title,
        subtitle: `${getDocumentTypeLabel(document.type)} · ${getDocumentStatusLabel(document.status)}`,
        companyDocumentId: document.companyDocumentId,
        searchText: [
            document.title,
            document.patientName,
            document.counterpartyName,
            document.referenceNumber,
            document.companyDocumentId,
            document.tags?.join(' '),
        ].filter(Boolean).join(' ').toLowerCase(),
        path: document.route || `/finance/documentale?doc=${document.id}`,
    }));

    const playbookEntries = playbooks.map((playbook) => ({
        id: playbook.id,
        kind: 'playbook',
        title: playbook.title,
        subtitle: `Wizard · ${playbook.steps.length} step`,
        companyDocumentId: playbook.companyDocumentId,
        searchText: [
            playbook.title,
            playbook.description,
            playbook.steps.map((step) => step.title).join(' '),
        ].join(' ').toLowerCase(),
        path: `/finance/processi?playbook=${playbook.id}`,
    }));

    const companyEntries = companies.map((company) => ({
        id: company.documentId,
        kind: 'company',
        title: company.name,
        subtitle: `Azienda · ${company.alias}`,
        companyDocumentId: company.documentId,
        searchText: [company.name, company.alias, company.documentId].join(' ').toLowerCase(),
        path: `/finance/shell`,
    }));

    const treasuryEntries = [
        ...((treasuryWorkspace?.sources || []).map((source) => ({
            id: source.id,
            kind: 'treasury-source',
            title: source.name,
            subtitle: `Tesoreria · ${source.alias}`,
            companyDocumentId: source.companyDocumentId,
            searchText: [source.name, source.alias, source.type].join(' ').toLowerCase(),
            path: `/finance/tesoreria?source=${source.id}`,
        }))),
        ...((treasuryWorkspace?.payables || []).map((payable) => ({
            id: payable.id,
            kind: 'treasury-payable',
            title: payable.label,
            subtitle: `Scadenziario · ${payable.supplierName}`,
            companyDocumentId: payable.companyDocumentId,
            searchText: [
                payable.label,
                payable.supplierName,
                payable.dueDate,
                payable.status,
            ].join(' ').toLowerCase(),
            path: `/finance/tesoreria?payable=${payable.id}`,
        }))),
    ];

    const inventoryEntries = [
        ...((inventoryWorkspace?.serials || []).map((serial) => ({
            id: serial.id,
            kind: 'inventory-serial',
            title: serial.serialNumber,
            subtitle: `Matricola · ${serial.patientName || serial.articleCode}`,
            companyDocumentId: 'aurifon',
            searchText: [
                serial.serialNumber,
                serial.articleCode,
                serial.patientName,
                serial.status,
            ].filter(Boolean).join(' ').toLowerCase(),
            path: `/finance/magazzino?serial=${serial.id}`,
        }))),
        ...((inventoryWorkspace?.deposits || []).map((deposit) => ({
            id: deposit.id,
            kind: 'inventory-deposit',
            title: deposit.name,
            subtitle: `Deposito · ${deposit.type}`,
            companyDocumentId: deposit.companyDocumentId,
            searchText: [deposit.name, deposit.type].join(' ').toLowerCase(),
            path: `/finance/magazzino?deposit=${deposit.id}`,
        }))),
    ];

    return [...documentEntries, ...playbookEntries, ...companyEntries, ...treasuryEntries, ...inventoryEntries];
};
